import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader2, BookOpen, FileText } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

/**
 * Admin Playbooks — markdown viewer for /app/memory/*.md files.
 * Sidebar lists playbooks, main pane renders the selected one.
 */
export default function AdminPlaybooks() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [content, setContent] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/playbooks`, {
        headers: authHeaders(),
      });
      setList(res.data.playbooks || []);
      // Auto-select the first one
      if ((res.data.playbooks || []).length > 0 && !active) {
        setActive(res.data.playbooks[0].slug);
      }
    } catch {
      toast.error('Could not load playbooks list.');
    } finally {
      setLoadingList(false);
    }
  }, [active]);

  const loadContent = useCallback(async (slug) => {
    if (!slug) return;
    setLoadingContent(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/playbooks/${slug}`, {
        headers: authHeaders(),
      });
      setContent(res.data.content || '');
    } catch {
      toast.error('Could not load playbook.');
      setContent('');
    } finally {
      setLoadingContent(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (active) loadContent(active); }, [active, loadContent]);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="admin-playbooks">
        <header className="mb-5">
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-[#00F0FF]" />
            Playbooks
          </h1>
          <p className="text-sm text-neutral-400 mt-1">
            Strategy docs from <code className="text-neutral-300">/app/memory/</code>. Read-only.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
          {/* Sidebar */}
          <aside className="bg-neutral-900/60 border border-white/10 rounded-xl overflow-hidden">
            {loadingList ? (
              <div className="p-6 text-center text-neutral-500 text-sm">
                <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" />
                Loading…
              </div>
            ) : list.length === 0 ? (
              <div className="p-6 text-center text-neutral-500 text-sm">
                No playbooks yet.
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {list.map((p) => (
                  <li key={p.slug}>
                    <button
                      onClick={() => setActive(p.slug)}
                      className={`w-full text-left px-4 py-3 hover:bg-white/5 transition flex items-center gap-2 text-sm ${
                        active === p.slug
                          ? 'bg-[#00F0FF]/10 border-l-2 border-[#00F0FF] text-[#00F0FF]'
                          : 'text-neutral-200 border-l-2 border-transparent'
                      }`}
                      data-testid={`playbook-tab-${p.slug}`}
                    >
                      <FileText className="w-4 h-4 shrink-0 opacity-70" />
                      <span className="flex-1 truncate" title={p.title}>{p.title}</span>
                      <span className="text-[10px] text-neutral-500 whitespace-nowrap">
                        {p.size_kb} KB
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Content */}
          <main className="bg-neutral-900/40 border border-white/10 rounded-xl p-6 md:p-8 min-h-[60vh]">
            {loadingContent ? (
              <div className="text-center text-neutral-500 py-20">
                <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
                Loading playbook…
              </div>
            ) : !content ? (
              <div className="text-center text-neutral-500 py-20 text-sm">
                Select a playbook from the sidebar to view it.
              </div>
            ) : (
              <article
                className="prose prose-invert max-w-none prose-headings:text-white prose-p:text-neutral-300 prose-li:text-neutral-300 prose-strong:text-white prose-a:text-[#00F0FF] prose-a:no-underline hover:prose-a:underline prose-table:text-sm prose-th:text-white prose-th:bg-neutral-900 prose-td:border-neutral-800 prose-th:border-neutral-800 prose-code:text-[#00F0FF] prose-code:bg-neutral-950 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:hidden prose-code:after:hidden prose-blockquote:border-l-[#00F0FF] prose-blockquote:text-neutral-300 prose-hr:border-white/10"
                data-testid="playbook-content"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
              </article>
            )}
          </main>
        </div>
      </div>
    </AdminLayout>
  );
}
